// A workspace's webhook receiver, registered and edited from the Integrations page (#1073).
//
// Over slice 3's API (#1072): a receiver is a URL and the events it is sent, and **no
// credential leaves the browser**. The server makes the signing secret and answers it once,
// on the create. This flow shows it once, in a read-only field with Copy, and drops it when
// the flow closes: it is held in this component's state and nowhere else, so nothing a later
// render reads — the list, a parent's state, the address, storage — can show it again. The
// list says the secret's prefix (`secret_prefix`), which is all a person needs to tell which
// secret their receiver holds.
//
// **Registering needs a step-up for `webhook_create`** (#1072), proven in this flow after
// Create and spent on the one request it opens; a grant for any other purpose does not open
// the route. An edit changes the URL and the events through `PATCH`, never the secret, and
// asks for none.
//
// The form refuses two things before it posts, each in a sentence tied to its control: an
// address that is not `https://`, and no event ticked. Everything else the server refuses —
// a private address, a URL another receiver sends to, the limit of five — is shown in the
// server's words, with the form left as it was typed.

import { useEffect, useRef, useState, type FormEvent } from "react";

import { messageOf } from "../api/http";
import {
  SECRET_OVERLAP_HOURS,
  WEBHOOK_CREATE_PURPOSE,
  WEBHOOK_EVENTS,
  WEBHOOK_ROTATE_PURPOSE,
  type CreatedWebhook,
  type RotatedWebhook,
  type WebhookConfig,
  type WebhookPatch,
  type WebhookRow,
} from "../api/integrations";
import type { StepUp } from "../api/policy";
import { Field, Notice } from "./chrome";
import { readableTime } from "./runPresentation";
import { StepFlow } from "./StepFlow";
import { StepUpDialog, useStepUpAttempt, useStepUpFactors, type BeginPasskey, type StepUpFactors } from "./StepUp";

/** Every wire name the form offers, in the order it offers them: what a body lists them in. */
const OFFERED: readonly string[] = WEBHOOK_EVENTS.flatMap(({ events }) => events.map(({ wire }) => wire));

/** Why the address cannot be a receiver's, or null when the form has nothing to say. */
export function webhookUrlProblem(raw: string): string | null {
  const typed = raw.trim();
  if (typed === "") return "Give the address your receiver listens on. It starts with https://.";
  if (!/^https:\/\//i.test(typed)) {
    return "The endpoint URL must start with https://. A receiver is sent your audit log, so it is only ever sent over an encrypted connection.";
  }
  try {
    if (new URL(typed).hostname === "") throw new Error("no host");
  } catch {
    return `“${typed}” is not a web address. It looks like https://siem.example.com/hooks/torque.`;
  }
  return null;
}

/** Why the events cannot be sent, or null. */
export const webhookEventsProblem = (events: ReadonlySet<string>): string | null =>
  events.size === 0 ? "Tick at least one event for this receiver to be sent." : null;

/** The settings a form holds, as the wire takes them: the events in the order offered. */
export const webhookConfigOf = (url: string, events: ReadonlySet<string>): WebhookConfig => ({
  url: url.trim(),
  events: OFFERED.filter((wire) => events.has(wire)),
});

const STEPS = [
  { id: "details", label: "Address and events" },
  { id: "secret", label: "Copy the signing secret" },
] as const;
type Step = (typeof STEPS)[number]["id"];

const EDIT_STEPS = [{ id: "details", label: "Address and events" }] as const;

/** The URL and the events, as both flows ask for them. */
function WebhookFields({
  idPrefix,
  url,
  events,
  urlProblem,
  eventsProblem,
  disabled,
  urlRef,
  eventsRef,
  onUrl,
  onEvent,
}: {
  idPrefix: string;
  url: string;
  events: ReadonlySet<string>;
  urlProblem: string | null;
  eventsProblem: string | null;
  disabled: boolean;
  urlRef: React.RefObject<HTMLInputElement | null>;
  eventsRef: React.RefObject<HTMLFieldSetElement | null>;
  onUrl: (value: string) => void;
  onEvent: (wire: string, on: boolean) => void;
}) {
  const hintId = `${idPrefix}-events-hint`;
  const errorId = `${idPrefix}-events-error`;
  return (
    <div className="settings-fields">
      <Field
        ref={urlRef}
        id={`${idPrefix}-url`}
        label="Endpoint URL"
        type="url"
        inputMode="url"
        autoComplete="off"
        spellCheck={false}
        disabled={disabled}
        value={url}
        hint="The https address your receiver listens on."
        error={urlProblem}
        onChange={(event) => onUrl(event.target.value)}
      />
      <fieldset ref={eventsRef} tabIndex={-1} aria-describedby={eventsProblem === null ? hintId : `${hintId} ${errorId}`} aria-invalid={eventsProblem === null ? undefined : true}>
        <legend className="field__label">Events</legend>
        <small id={hintId} className="field__hint">
          What this receiver is sent. Each is signed with its secret.
        </small>
        {WEBHOOK_EVENTS.map(({ group, events: offered }) => (
          <fieldset key={group}>
            <legend>{group}</legend>
            {offered.map(({ wire, words }) => (
              <label key={wire} className="checkline">
                <input type="checkbox" disabled={disabled} checked={events.has(wire)} onChange={(event) => onEvent(wire, event.target.checked)} />
                <span>
                  {words} <code>{wire}</code>
                </span>
              </label>
            ))}
          </fieldset>
        ))}
        {eventsProblem === null ? null : (
          <small id={errorId} className="field__error" role="alert">
            {eventsProblem}
          </small>
        )}
      </fieldset>
    </div>
  );
}

/** The form's two refusals, held together so one Create checks both. */
function useWebhookForm(initial: WebhookConfig | null) {
  const [url, setUrl] = useState(initial?.url ?? "");
  const [events, setEvents] = useState<ReadonlySet<string>>(() => new Set(initial?.events ?? []));
  const [urlProblem, setUrlProblem] = useState<string | null>(null);
  const [eventsProblem, setEventsProblem] = useState<string | null>(null);
  const urlRef = useRef<HTMLInputElement | null>(null);
  const eventsRef = useRef<HTMLFieldSetElement | null>(null);
  /** The config to send, or null with each refusal shown and focus on the first. */
  const checked = (): WebhookConfig | null => {
    const badUrl = webhookUrlProblem(url);
    const badEvents = webhookEventsProblem(events);
    setUrlProblem(badUrl);
    setEventsProblem(badEvents);
    if (badUrl !== null) urlRef.current?.focus();
    else if (badEvents !== null) eventsRef.current?.focus();
    return badUrl === null && badEvents === null ? webhookConfigOf(url, events) : null;
  };
  const fields = (idPrefix: string, disabled: boolean) => (
    <WebhookFields
      idPrefix={idPrefix}
      url={url}
      events={events}
      urlProblem={urlProblem}
      eventsProblem={eventsProblem}
      disabled={disabled}
      urlRef={urlRef}
      eventsRef={eventsRef}
      onUrl={(value) => {
        setUrl(value);
        setUrlProblem(null);
      }}
      onEvent={(wire, on) => {
        setEvents((was) => {
          const next = new Set(was);
          if (on) next.add(wire);
          else next.delete(wire);
          return next;
        });
        setEventsProblem(null);
      }}
    />
  );
  return { url, events, checked, fields };
}

/**
 * A secret shown once (#1073, #1075): a read-only field with Copy, focused and selected when it
 * is drawn, and Done. The secret is the caller's state and is dropped when Done closes it.
 */
function ShownOnce({ id, label, secret, onDone }: { id: string; label: string; secret: string; onDone: () => void }) {
  const [copied, setCopied] = useState<string | null>(null);
  const field = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    field.current?.focus();
    field.current?.select();
  }, [secret]);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(secret);
      setCopied("Copied to the clipboard.");
    } catch {
      field.current?.focus();
      field.current?.select();
      setCopied("This browser would not copy it. The secret is selected: copy it from the field.");
    }
  };

  return (
    <>
      <p className="field">
        <label htmlFor={id} className="field__label">
          {label}
        </label>
        <input ref={field} id={id} className="input" readOnly value={secret} autoComplete="off" spellCheck={false} aria-describedby={`${id}-hint`} />
        <small id={`${id}-hint`} className="field__hint">
          This is the only time it is shown. Copy it into your receiver now: it will not be shown again, here or anywhere.
        </small>
      </p>
      <div className="actions">
        <button type="button" className="btn" onClick={() => void copy()}>
          Copy
        </button>
        <button type="button" className="btn btn--primary" onClick={onDone}>
          Done
        </button>
      </div>
      {copied === null ? null : (
        <small className="field__hint" role="status">
          {copied}
        </small>
      )}
    </>
  );
}

/**
 * Connect webhook: the address and the events, Create, the step-up, and the secret once.
 * Closing the flow — Done, or Cancel before the save — drops everything it held.
 */
export function ConnectWebhook({
  onRegister,
  stepUpFactors,
  beginPasskey,
  onClose,
}: {
  onRegister: (config: WebhookConfig, stepUp: StepUp) => Promise<CreatedWebhook>;
  stepUpFactors?: () => Promise<StepUpFactors>;
  beginPasskey?: BeginPasskey;
  onClose: () => void;
}) {
  const form = useWebhookForm(null);
  const factors = useStepUpFactors(stepUpFactors);
  const { proof, attempt, unproven, setProof, spent } = useStepUpAttempt();
  const [confirming, setConfirming] = useState(false);
  const [saving, setSaving] = useState(false);
  const [refusal, setRefusal] = useState<string | null>(null);
  // The one place the secret is held: this flow's own state, gone when the flow closes.
  const [made, setMade] = useState<{ url: string; secret: string } | null>(null);
  const create = useRef<HTMLButtonElement | null>(null);
  const heading = useRef<HTMLHeadingElement | null>(null);
  const step: Step = made === null ? "details" : "secret";

  // Opened by Connect webhook, which goes when the flow opens: focus moves to the flow's
  // heading, so the next Tab is the address and not the top of the page.
  useEffect(() => {
    heading.current?.focus();
  }, []);

  const onCreate = (event: FormEvent) => {
    event.preventDefault();
    setRefusal(null);
    if (form.checked() === null) {
      setConfirming(false);
      return;
    }
    setConfirming(true);
  };

  const cancel = () => {
    setConfirming(false);
    spent();
    create.current?.focus();
  };

  const register = async () => {
    const config = form.checked();
    if (config === null) {
      setConfirming(false);
      return;
    }
    if (proof === null) return;
    setSaving(true);
    setRefusal(null);
    try {
      const answered = await onRegister(config, proof);
      setMade({ url: answered.config.url, secret: answered.secret });
      setConfirming(false);
    } catch (refused) {
      // The grant went with that request; the next attempt proves again.
      spent();
      setRefusal(`${messageOf(refused)} Nothing was registered.`);
    } finally {
      setSaving(false);
    }
  };

  const done = () => {
    setMade(null);
    onClose();
  };

  if (made !== null) {
    return (
      <StepFlow title="Connect a webhook" testId="flow-webhook" steps={STEPS} current={step}>
        <h4 className="step-flow__title">Copy the signing secret</h4>
        <p>
          Webhook registered. It sends to <code className="break">{made.url}</code>.
        </p>
        <ShownOnce id="webhook-secret" label="Signing secret" secret={made.secret} onDone={done} />
      </StepFlow>
    );
  }

  return (
    <StepFlow title="Connect a webhook" testId="flow-webhook" steps={STEPS} current={step}>
      <h4 ref={heading} tabIndex={-1} className="step-flow__title">
        Address and events
      </h4>
      <form noValidate onSubmit={onCreate} className="stack stack--tight" aria-label="Address and events">
        <p className="muted">Each event is posted to your receiver as JSON, signed with a secret this page shows you once.</p>
        {form.fields("webhook", saving)}
        {refusal === null ? null : <Notice tone="bad">{refusal}</Notice>}
        <div className="actions">
          <button ref={create} type="submit" className="btn btn--primary" disabled={saving}>
            Create
          </button>
          {confirming ? null : (
            <button type="button" className="btn" onClick={onClose}>
              Cancel
            </button>
          )}
        </div>
      </form>
      {confirming ? (
        <>
          <StepUpDialog
            key={attempt}
            role="group"
            autoFocus
            purpose={WEBHOOK_CREATE_PURPOSE}
            boundTo="registering this webhook"
            factors={factors}
            beginPasskey={beginPasskey}
            disabled={saving}
            onProofChange={setProof}
          />
          <div className="actions">
            <button type="button" className="btn btn--primary" disabled={unproven || saving} aria-busy={saving} onClick={() => void register()}>
              {saving ? "Registering…" : "Register webhook"}
            </button>
            <button type="button" className="btn" disabled={saving} onClick={cancel}>
              Cancel
            </button>
          </div>
        </>
      ) : null}
    </StepFlow>
  );
}

/** Edit: the URL and the events, through `PATCH`. It never asks for, or shows, a secret. */
export function EditWebhook({
  row,
  onUpdate,
  onClose,
}: {
  row: WebhookRow;
  onUpdate: (patch: WebhookPatch) => Promise<void>;
  onClose: () => void;
}) {
  const form = useWebhookForm(row.config);
  const [saving, setSaving] = useState(false);
  const [said, setSaid] = useState<{ tone: "bad" | "good"; text: string } | null>(null);
  const heading = useRef<HTMLHeadingElement | null>(null);
  // Opened by the row's Edit: focus moves into the flow, which is drawn below the list.
  useEffect(() => {
    heading.current?.focus();
  }, []);

  const save = async (event: FormEvent) => {
    event.preventDefault();
    setSaid(null);
    const config = form.checked();
    if (config === null) return;
    const same =
      config.url === row.config.url &&
      config.events.length === row.config.events.length &&
      config.events.every((wire) => row.config.events.includes(wire));
    if (same) {
      setSaid({ tone: "good", text: "Nothing has changed, so nothing was saved." });
      return;
    }
    setSaving(true);
    try {
      await onUpdate({ config });
      onClose();
    } catch (refused) {
      setSaid({ tone: "bad", text: `${messageOf(refused)} The webhook was not changed.` });
    } finally {
      setSaving(false);
    }
  };

  return (
    <StepFlow title="Edit webhook" testId="flow-webhook-edit" steps={EDIT_STEPS} current="details">
      <h4 ref={heading} tabIndex={-1} className="step-flow__title">
        Address and events
      </h4>
      <form noValidate onSubmit={(event) => void save(event)} className="stack stack--tight" aria-label="Address and events">
        <p className="muted">Its signing secret stays as it is. It is never shown again.</p>
        {form.fields("webhook-edit", saving)}
        {said === null ? null : <Notice tone={said.tone}>{said.text}</Notice>}
        <div className="actions">
          <button type="submit" className="btn btn--primary" disabled={saving} aria-busy={saving}>
            {saving ? "Saving…" : "Save"}
          </button>
          <button type="button" className="btn" disabled={saving} onClick={onClose}>
            Cancel
          </button>
        </div>
      </form>
    </StepFlow>
  );
}

const ROTATE_STEPS = [
  { id: "confirm", label: "Confirm" },
  { id: "secret", label: "Copy the new secret" },
] as const;

/**
 * Rotate secret (#1075): a confirmation that says both secrets sign for the overlap, the
 * step-up for `webhook_rotate`, then the new secret once. Cancel at either posts nothing. The
 * new secret is held in this flow's state alone and is gone when the flow closes.
 */
export function RotateWebhookSecret({
  row,
  onRotate,
  stepUpFactors,
  beginPasskey,
  onClose,
}: {
  row: WebhookRow;
  onRotate: (id: string, stepUp: StepUp) => Promise<RotatedWebhook>;
  stepUpFactors?: () => Promise<StepUpFactors>;
  beginPasskey?: BeginPasskey;
  onClose: () => void;
}) {
  const factors = useStepUpFactors(stepUpFactors);
  const { proof, attempt, unproven, setProof, spent } = useStepUpAttempt();
  const [proving, setProving] = useState(false);
  const [saving, setSaving] = useState(false);
  const [refusal, setRefusal] = useState<string | null>(null);
  const [rotated, setRotated] = useState<RotatedWebhook | null>(null);
  const heading = useRef<HTMLHeadingElement | null>(null);

  // Opened from the row: focus moves to the flow's heading, drawn below the list.
  useEffect(() => {
    heading.current?.focus();
  }, []);

  const cancel = () => {
    spent();
    onClose();
  };

  const rotate = async () => {
    if (proof === null) return;
    setSaving(true);
    setRefusal(null);
    try {
      setRotated(await onRotate(row.id, proof));
    } catch (refused) {
      // The grant went with that request; the next attempt proves again.
      spent();
      setRefusal(`${messageOf(refused)} Nothing was rotated.`);
    } finally {
      setSaving(false);
    }
  };

  if (rotated !== null) {
    return (
      <StepFlow title="Rotate the signing secret" testId="flow-webhook-rotate" steps={ROTATE_STEPS} current="secret">
        <h4 className="step-flow__title">Copy the new secret</h4>
        <p>
          Secret rotated for <code className="break">{row.config.url}</code>.{" "}
          {rotated.earlier_secret_stopped_signing ? "The secret before the one just replaced stopped signing now. " : ""}
          {rotated.previous_secret_until == null ? null : (
            <>
              The previous secret also signs until <time dateTime={rotated.previous_secret_until}>{readableTime(rotated.previous_secret_until)}</time>.
            </>
          )}
        </p>
        <ShownOnce id="webhook-rotated-secret" label="New signing secret" secret={rotated.secret} onDone={onClose} />
      </StepFlow>
    );
  }

  return (
    <StepFlow title="Rotate the signing secret" testId="flow-webhook-rotate" steps={ROTATE_STEPS} current="confirm">
      <h4 ref={heading} tabIndex={-1} className="step-flow__title">
        Confirm the rotation
      </h4>
      <p data-testid="rotate-confirmation">
        A new signing secret is made for <code className="break">{row.config.url}</code> and shown once. For {SECRET_OVERLAP_HOURS} hours
        every delivery is signed with both secrets, the new one and the one it replaces, so your receiver goes on verifying while you
        give it the new one. After that only the new secret signs.
      </p>
      {refusal === null ? null : <Notice tone="bad">{refusal}</Notice>}
      {proving ? (
        <>
          <StepUpDialog
            key={attempt}
            role="group"
            autoFocus
            purpose={WEBHOOK_ROTATE_PURPOSE}
            boundTo="rotating this webhook's secret"
            factors={factors}
            beginPasskey={beginPasskey}
            disabled={saving}
            onProofChange={setProof}
          />
          <div className="actions">
            <button type="button" className="btn btn--primary" disabled={unproven || saving} aria-busy={saving} onClick={() => void rotate()}>
              {saving ? "Rotating…" : "Rotate now"}
            </button>
            <button type="button" className="btn" disabled={saving} onClick={cancel}>
              Cancel
            </button>
          </div>
        </>
      ) : (
        <div className="actions">
          <button type="button" className="btn btn--primary" onClick={() => setProving(true)}>
            Continue
          </button>
          <button type="button" className="btn" onClick={cancel}>
            Cancel
          </button>
        </div>
      )}
    </StepFlow>
  );
}
